/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { takeUntil, switchMap, catchError, finalize } from 'rxjs/operators';
import { DocumentationService } from '../../services/documentation.service';
import { MarkdownRendererComponent } from '../../../shared/components/markdown-renderer/markdown-renderer.component';
import {
  DocumentationContent,
  DocumentationError,
  DocumentationMenuState
} from '../../models/documentation.interface';

/**
 * Container component for documentation display.
 * Handles content loading, navigation context, and error handling.
 */
@Component({
  selector: 'app-documentation-viewer',
  standalone: true,
  imports: [CommonModule, MarkdownRendererComponent],
  templateUrl: './documentation-viewer.component.html',
  styleUrl: './documentation-viewer.component.scss'
})
export class DocumentationViewerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() documentationRoute: string = '';

  content: DocumentationContent | null = null;
  markdownContent: string = '';
  loading: boolean = false;
  error: DocumentationError | null = null;
  menuState: DocumentationMenuState | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private documentationService: DocumentationService
  ) {}

  ngOnInit(): void {
    this.setupRouteListener();
    this.setupMenuStateListener();

    // Load content if documentationRoute is provided
    if (this.documentationRoute) {
      this.loadDocumentationContent(this.documentationRoute);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentationRoute'] && changes['documentationRoute'].currentValue) {

      this.loadDocumentationContent(changes['documentationRoute'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load documentation content for a specific route
   */
  private loadDocumentationContent(route: string): void {
    if (!route) {

      this.content = null;
      this.markdownContent = '';
      return;
    }


    this.loading = true;
    this.error = null;

    // Update active section in menu service
    this.documentationService.setActiveRoute(route);

    // Get documentation content by route (handles file path resolution)
    this.documentationService.getDocumentationContentByRoute(route).pipe(
      catchError(error => {
        this.error = {
          type: 'load-error',
          message: `Failed to load documentation: ${error.message}`,
          details: error
        };
        return of(null);
      }),
      finalize(() => {
        this.loading = false;
      }),
      takeUntil(this.destroy$)
    ).subscribe((content: string | null) => {
      this.markdownContent = content || '';

      // Scroll to top when new content is loaded
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);



      // Create a simple content object for navigation
      if (content) {
        this.content = {
          id: route,
          title: route,
          content: content,
          metadata: {
            filePath: route,
            crossReferences: []
          },
          navigation: {
            previous: undefined,
            next: undefined,
            parent: undefined,
            children: []
          }
        };
      } else {
        this.content = null;
      }
    });
  }

  /**
   * Setup listener for route parameter changes
   */
  private setupRouteListener(): void {
    this.route.paramMap.pipe(
      takeUntil(this.destroy$),
      switchMap(params => {
        // Only use route parameters if no input route is provided
        if (this.documentationRoute) {

          return of(null);
        }

        // Try to get route parameter first, then section parameter
        const route = params.get('route') || params.get('section') || '';


        if (!route) {

          return of(null);
        }
        this.loadDocumentationContent(route);
        return of(null); // We handle loading in loadDocumentationContent
      })
    ).subscribe();
  }

  /**
   * Setup listener for menu state changes
   */
  private setupMenuStateListener(): void {
    this.documentationService.getMenuState().pipe(
      takeUntil(this.destroy$)
    ).subscribe(menuState => {
      this.menuState = menuState;
    });
  }

  /**
   * Navigate to a specific documentation route
   */
  navigateToRoute(route: string): void {
    this.router.navigate(['/api-mesh/docs', route]);
  }

  /**
   * Navigate to parent section
   */
  navigateToParent(): void {
    if (this.content?.navigation.parent) {
      this.navigateToRoute(this.content.navigation.parent.route);
    }
  }

  /**
   * Navigate to next sibling
   */
  navigateToNext(): void {
    if (this.content?.navigation.next) {
      this.navigateToRoute(this.content.navigation.next.route);
    }
  }

  /**
   * Navigate to previous sibling
   */
  navigateToPrevious(): void {
    if (this.content?.navigation.previous) {
      this.navigateToRoute(this.content.navigation.previous.route);
    }
  }

  /**
   * Get breadcrumb navigation
   */
  getBreadcrumbs(): Array<{title: string, route: string}> {
    if (!this.content) return [];

    const breadcrumbs: Array<{title: string, route: string}> = [];
    let current = this.content.navigation.parent;

    while (current) {
      breadcrumbs.unshift({
        title: current.title,
        route: current.route
      });
      current = current.parent;
    }

    return breadcrumbs;
  }

  /**
   * Check if content has child sections
   */
  hasChildren(): boolean {
    return !!(this.content?.navigation.children?.length);
  }

  /**
   * Check if navigation exists for a direction
   */
  hasNavigation(direction: 'previous' | 'next' | 'parent'): boolean {
    return !!(this.content?.navigation[direction]);
  }

  /**
   * Navigate to breadcrumb
   */
  navigateToBreadcrumb(route: string): void {
    this.navigateToRoute(route);
  }

  /**
   * Navigate to child section
   */
  navigateToChild(child: any): void {
    this.navigateToRoute(child.route);
  }

  /**
   * Reload current content
   */
  reloadContent(): void {
    const currentRoute = this.route.snapshot.paramMap.get('route') || this.route.snapshot.paramMap.get('section');
    if (currentRoute) {
      this.documentationService.clearCache();
      this.documentationService.setActiveRoute(currentRoute);
    }
  }
}
